/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ResizablePanel } from '@opengeoweb/shared';
import type { ResizablePanelWrapperProps } from '@opengeoweb/shared';
import { timeSeriesActions } from '../../store/reducer';
import { getPlotIsOpen, getPlotCustomSize } from '../../store/selectors';
import { TimeSeriesActionOrigin } from '../../store/types';
import type { TimeSeriesModuleState } from '../../store/types';

export const TIME_SERIES_PANEL_MIN_HEIGHT = 90;

export const TimeSeriesPanelConnect = React.forwardRef<
  HTMLDivElement,
  ResizablePanelWrapperProps
>(
  (
    {
      panelId: plotId,
      viewId,
      name,
      children,
      headerAction,
      isDisplayed,
      maxWidth,
      minWidth,
      handleButtonComponent,
      onOpenChange,
      onResizeStart,
      onResizeEnd,
      initialSize,
      allowOverflow,
      onSizeChange,
    },
    ref,
  ): React.ReactElement => {
    if (!plotId || !viewId) {
      throw new Error(
        'TimeSeriesPanelConnect requires both `plotId` and `viewId` props. ' +
          'Use it inside a SortablePanels that has `viewId` set, or pass both directly.',
      );
    }
    const dispatch = useDispatch();

    const isOpen = useSelector((store: TimeSeriesModuleState) =>
      getPlotIsOpen(store, viewId, plotId),
    );
    const customSize = useSelector((store: TimeSeriesModuleState) =>
      getPlotCustomSize(store, viewId, plotId),
    );

    const handleSetIsOpen = React.useCallback(
      (open: boolean) => {
        if (isOpen === open) {
          return;
        }
        dispatch(
          timeSeriesActions.setPlotIsOpen({
            plotId,
            panelId: viewId,
            origin: TimeSeriesActionOrigin.timeSeriesManager,
            isOpen: open,
          }),
        );
        onOpenChange?.(open);
      },
      [isOpen, dispatch, plotId, viewId, onOpenChange],
    );

    const handleSetCustomSize = React.useCallback(
      (size: number) => {
        onSizeChange?.(size);
        dispatch(
          timeSeriesActions.setPlotSize({
            plotId,
            panelId: viewId,
            origin: TimeSeriesActionOrigin.timeSeriesManager,
            size,
          }),
        );
      },
      [dispatch, plotId, viewId, onSizeChange],
    );

    return (
      <ResizablePanel
        allowOverflow={allowOverflow}
        customWidth={customSize}
        direction="vertical"
        handleButtonComponent={handleButtonComponent}
        headerAction={headerAction}
        initialSize={initialSize}
        isDisplayed={isDisplayed}
        isOpen={isOpen}
        maxWidth={maxWidth}
        minWidth={minWidth ?? TIME_SERIES_PANEL_MIN_HEIGHT}
        name={name}
        onResizeEnd={onResizeEnd}
        onResizeStart={onResizeStart}
        onSetCustomWidth={handleSetCustomSize}
        onSetIsOpen={handleSetIsOpen}
        ref={ref}
      >
        {children}
      </ResizablePanel>
    );
  },
);
